import type { AddressTier } from "@solomon-labs/design/components/ui";

/**
 * Width budget per rung for a base58 address in ABC Social (our body/small
 * face), in em. ABC Social is proportional, so this is the widest seen across
 * two runs of 3,000 random addresses at 16px (font loaded), plus ~3%. A string
 * wider still (a run of W/M) falls back to the DS `Address` ellipsis rather
 * than clipping silently.
 */
const RUNG_EM = {
    full: 27.5, // 44 chars
    12: 16.9, // 12...12
    10: 14.3, // 10...10
    8: 11.8, // 8...8
    6: 9.4, // 6...6
    4: 6.8 // 4..4
} as const;

/**
 * Breakpoints for the DS `Address` element set in ABC Social, i.e. rendered with
 * `textVariant="small"` / `"body"` and `mono={false}`. `fontPx` is the size the
 * address renders at on desktop; smaller mobile type only makes every rung fit
 * more easily.
 */
export function addressTiersFor(fontPx: number): AddressTier[] {
    const at = (em: number): number => Math.ceil(em * fontPx);
    return [
        { minWidth: at(RUNG_EM.full) },
        { minWidth: at(RUNG_EM[12]), head: 12, tail: 12, separator: "..." },
        { minWidth: at(RUNG_EM[10]), head: 10, tail: 10, separator: "..." },
        { minWidth: at(RUNG_EM[8]), head: 8, tail: 8, separator: "..." },
        { minWidth: at(RUNG_EM[6]), head: 6, tail: 6, separator: "..." },
        { minWidth: at(RUNG_EM[4]), head: 4, tail: 4, separator: ".." },
        { minWidth: 0, head: 2, tail: 4, separator: ".." }
    ];
}
